import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug?: string }>();

  const currentLang = i18n.language as "en" | "pt" | "de";
  const safeLang = ["en", "pt", "de"].includes(currentLang) ? currentLang : "en";

  const article = slug ? articles.find((a) => a.slug === slug) : articles[0];
  const fallbackArticle = articles[0];
  const displayArticle = article || fallbackArticle;

  const title = displayArticle.title[safeLang] || displayArticle.title.en;
  const excerpt = displayArticle.excerpt[safeLang] || displayArticle.excerpt.en;

  const faqs = [
    { q: t("faq_q1"), a: t("faq_a1") },
    { q: t("faq_q2"), a: t("faq_a2") },
    { q: t("faq_q3"), a: t("faq_a3") },
    { q: t("faq_q4"), a: t("faq_a4") },
    { q: t("faq_q5"), a: t("faq_a5") },
    { q: t("faq_q6"), a: t("faq_a6") },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-8 pb-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-600">
            <a href="/" className="hover:text-foreground-950 transition-colors">{t("article_breadcrumb_home")}</a>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-950">{t("article_breadcrumb_guide")}</span>
          </nav>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <header className="mb-10">
            <span className="inline-block text-xs font-semibold tracking-widest text-primary-600 uppercase mb-3">CSSBuy Spreadsheet</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">{title}</h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>{displayArticle.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300" />
              <span>{displayArticle.readTime} read</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300" />
              <span className="inline-block px-2 py-0.5 rounded-md text-xs font-medium bg-background-100 text-foreground-600">{displayArticle.tag}</span>
            </div>
          </header>

          <div className="rounded-xl overflow-hidden border border-background-200/60 mb-10">
            <img
              src={displayArticle.image}
              alt={title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="bg-white rounded-xl border border-background-200/60 p-6 md:p-8 mb-10">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">{excerpt}</p>
          </div>

          <div className="bg-background-100 rounded-xl p-6 mb-10">
            <h2 className="text-sm font-semibold text-foreground-950 uppercase tracking-wide mb-4">{t("article_toc")}</h2>
            <ul className="space-y-2">
              <li><a href="#what" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">1. {t("article_h2_what")}</a></li>
              <li><a href="#works" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">2. {t("article_h2_works")}</a></li>
              <li><a href="#find" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">3. {t("article_h2_find")}</a></li>
              <li><a href="#qc" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">4. {t("article_h2_qc")}</a></li>
              <li><a href="#shipping" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">5. {t("article_h2_shipping")}</a></li>
              <li><a href="#safety" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">6. {t("article_h2_safety")}</a></li>
              <li><a href="#faq" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">7. {t("article_h2_faq")}</a></li>
            </ul>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 id="what" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">{t("article_h2_what")}</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p1")}</p>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p2")}</p>

            <div className="my-8">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20modern%20workspace%20with%20open%20laptop%20showing%20data%20dashboard%20clean%20white%20desk%20with%20small%20potted%20plant%20soft%20natural%20lighting%20editorial%20photography%20warm%20beige%20tones&width=1200&height=600&seq=article-body-01&orientation=landscape"
                alt="CSSBuy Spreadsheet"
                className="w-full rounded-xl"
              />
            </div>

            <h2 id="works" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">{t("article_h2_works")}</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p3")}</p>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p4")}</p>

            <h2 id="find" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">{t("article_h2_find")}</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p5")}</p>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p6")}</p>

            <div className="my-8">
              <img
                src="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20fashion%20shopping%20spree%20with%20colorful%20clothing%20items%20shoes%20and%20bags%20spread%20on%20white%20marble%20table%20soft%20natural%20lighting%20editorial%20flat%20lay%20photography%20warm%20tones&width=1200&height=600&seq=article-body-02&orientation=landscape"
                alt="CSSBuy Products"
                className="w-full rounded-xl"
              />
            </div>

            <h2 id="qc" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">{t("article_h2_qc")}</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p7")}</p>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p8")}</p>

            <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-6 my-8">
              <h3 className="text-lg font-semibold text-accent-900 mb-2">{t("qc_title")}</h3>
              <p className="text-accent-800 text-sm">{t("qc_text")}</p>
            </div>

            <h2 id="shipping" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">{t("article_h2_shipping")}</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p9")}</p>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p10")}</p>

            <h2 id="safety" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">{t("article_h2_safety")}</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p11")}</p>
            <p className="text-foreground-700 leading-relaxed mb-4">{t("article_p12")}</p>

            <div className="bg-secondary-50 border border-secondary-200/60 rounded-xl p-6 my-8">
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">{t("safety_title")}</h3>
              <ul className="space-y-2 mt-3">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-secondary-800">
                    <i className="ri-check-line mt-0.5 text-secondary-600" />
                    {t(`safety_item${i}`)}
                  </li>
                ))}
              </ul>
            </div>

            <h2 id="faq" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">{t("article_h2_faq")}</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white border border-background-200/60 rounded-xl p-5">
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{faq.q}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <h3 className="text-lg font-semibold text-foreground-950 mb-4">{t("article_read_more")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href="/" className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 text-sm px-4 py-3 rounded-lg hover:bg-primary-200 transition-colors whitespace-nowrap">
                <i className="ri-home-line" />
                {t("article_back_home")}
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 bg-accent-100 text-accent-700 text-sm px-4 py-3 rounded-lg hover:bg-accent-200 transition-colors whitespace-nowrap">
                <i className="ri-external-link-line" />
                {t("cta_main_site")}
              </a>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}